import { Controller } from "@hotwired/stimulus"

// Proposed Change Orders list: new-PCO modal (manual or AI quote upload with
// polling), CO-catcher scan trigger, suggestion accept/dismiss.
export default class extends Controller {
  static targets = ["newModal", "title", "reason", "markup", "days", "createBtn",
                    "quoteInput", "quoteStatus", "scanBtn", "suggestions", "scanResultsModal"]
  static values = {
    createUrl: String, presignUrl: String,
    aiQuoteUrl: String, aiQuoteStatusUrl: String,
    aiScanUrl: String, aiScanStatusUrl: String
  }

  connect() {
    const msg = sessionStorage.getItem("builder-finance.flash")
    if (msg) {
      sessionStorage.removeItem("builder-finance.flash")
      this._showToast(msg)
    }
    // A scan just finished (page reloaded with fresh suggestions) — pop the
    // FYI modal so the findings aren't missed.
    if (sessionStorage.getItem("gc-pco.scanResults")) {
      sessionStorage.removeItem("gc-pco.scanResults")
      if (this.hasScanResultsModalTarget) this.scanResultsModalTarget.classList.remove("hidden")
    }
  }

  closeScanResults() {
    this.scanResultsModalTarget.classList.add("hidden")
  }

  reviewScanResults() {
    this.scanResultsModalTarget.classList.add("hidden")
    if (this.hasSuggestionsTarget) this.suggestionsTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  disconnect() { clearInterval(this._poll) }

  openPco(e) {
    if (e.target.closest("button, a, input")) return
    window.location.href = e.currentTarget.dataset.url
  }

  openNewModal() { this.newModalTarget.classList.remove("hidden") }
  closeNewModal() { this.newModalTarget.classList.add("hidden") }
  stopPropagation(e) { e.stopPropagation() }

  async create() {
    this.createBtnTarget.disabled = true
    try {
      const resp = await fetch(this.createUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({
          title: this.titleTarget.value, reason: this.reasonTarget.value,
          markup_pct: this.markupTarget.value, time_extension_days: this.daysTarget.value
        })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not create the PCO")
      window.location.href = data.url
    } catch (err) {
      this._showToast(err.message, "alert")
      this.createBtnTarget.disabled = false
    }
  }

  // ── #15: sub quote upload → AI extraction → draft PCO ──
  async quoteChosen(e) {
    const file = e.target.files[0]
    if (!file) return
    e.target.value = ""
    this._quoteStatus(`Uploading ${file.name}…`)
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const u = pd.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed")

      this._quoteStatus("Reading the quote…")
      const start = await fetch(this.aiQuoteUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ s3_key: `uploads/tmp/${u.cache_name}`, filename: file.name, mime_type: u.content_type })
      })
      const sd = await start.json()
      if (!start.ok) throw new Error(sd.error || "Could not start the extraction")
      this._pollQuote(sd.run_id)
    } catch (err) {
      this._quoteStatus("")
      this._showToast(err.message, "alert")
    }
  }

  _pollQuote(runId) {
    let ticks = 0
    this._poll = setInterval(async () => {
      if (++ticks > 60) { clearInterval(this._poll); this._quoteStatus("Taking too long — try again."); return }
      const resp = await fetch(`${this.aiQuoteStatusUrlValue}?run_id=${runId}`, { headers: { "Accept": "application/json" } })
      const data = await resp.json()
      if (data.status === "done") {
        clearInterval(this._poll)
        if (data.result && data.result.url) {
          sessionStorage.setItem("builder-finance.flash", "Quote read — review the drafted PCO lines")
          window.location.href = data.result.url
        } else {
          this._quoteStatus("Done, but nothing could be extracted — create the PCO manually.")
        }
      } else if (data.status === "failed") {
        clearInterval(this._poll)
        this._quoteStatus("")
        this._showToast(data.error || "The quote couldn't be read — create the PCO manually.", "alert")
      } else {
        this._quoteStatus("Reading the quote…")
      }
    }, 2500)
  }

  _quoteStatus(text) {
    this.quoteStatusTarget.textContent = text
    this.quoteStatusTarget.classList.toggle("hidden", !text)
  }

  // ── A2: CO catcher scan ──
  async aiScan() {
    this.scanBtnTarget.disabled = true
    this.scanBtnTarget.innerHTML = "Scanning jobsite records…"
    try {
      const resp = await fetch(this.aiScanUrlValue, { method: "POST", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not start the scan")
      this._pollScan(data.run_id)
    } catch (err) {
      this._showToast(err.message, "alert")
      this._resetScanBtn()
    }
  }

  _pollScan(runId) {
    let ticks = 0
    this._poll = setInterval(async () => {
      if (++ticks > 90) { clearInterval(this._poll); this._resetScanBtn(); return }
      const resp = await fetch(`${this.aiScanStatusUrlValue}?run_id=${runId}`, { headers: { "Accept": "application/json" } })
      const data = await resp.json()
      if (data.status === "done") {
        clearInterval(this._poll)
        const count = (data.result && data.result.suggestion_count) || 0
        if (count > 0) {
          sessionStorage.setItem("gc-pco.scanResults", "1") // FYI modal opens after reload
        } else {
          sessionStorage.setItem("builder-finance.flash", "Scan complete — nothing unbilled found this time")
        }
        window.location.reload()
      } else if (data.status === "failed") {
        clearInterval(this._poll)
        this._showToast(data.error || "The scan didn't finish — try again later.", "alert")
        this._resetScanBtn()
      }
    }, 2500)
  }

  _resetScanBtn() {
    this.scanBtnTarget.disabled = false
    this.scanBtnTarget.innerHTML = "✨ Catch unbilled work"
  }

  async acceptSuggestion(e) {
    const url = e.currentTarget.dataset.url
    try {
      const resp = await fetch(url, { method: "POST", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not draft the PCO")
      sessionStorage.setItem("builder-finance.flash", "Drafted — review the PCO and send it to the owner")
      window.location.href = data.url
    } catch (err) {
      this._showToast(err.message, "alert")
    }
  }

  async dismissSuggestion(e) {
    const url = e.currentTarget.dataset.url
    const row = e.currentTarget.closest("[data-suggestion-id]")
    const resp = await fetch(url, { method: "POST", headers: this._headers() })
    if (resp.ok) {
      row.remove()
      if (this.hasSuggestionsTarget && !this.suggestionsTarget.querySelector("[data-suggestion-id]")) {
        this.suggestionsTarget.remove()
      }
    }
  }

  _headers() {
    return {
      "Content-Type": "application/json", "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    div.textContent = message
    document.body.prepend(div)
  }
}
